import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Badge, 
  Table, 
  Alert, 
  Spinner,
  Form,
  InputGroup,
  Dropdown,
  DropdownButton,
  Pagination
} from 'react-bootstrap';
import { 
  Search, 
  Download, 
  CreditCard, 
  Calendar, 
  User,
  FileText,
  RefreshCw,
  Filter
} from 'react-feather';
import SubscriptionService from '../../../services/SubscriptionService';
import { generateInvoice } from '../../../utils/invoiceGenerator';

const AdminBillingPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all'); // 'all', 'invoice', 'transaction', 'user'
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchPayments();
  }, [currentPage, statusFilter]);

  const fetchPayments = async (searchParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = (currentPage - 1) * itemsPerPage;
      let response;
      
      if (searchParams.invoice_id || searchParams.transaction_id || searchParams.status || searchParams.user_id) {
        response = await SubscriptionService.searchPayments({
          ...searchParams,
          limit: itemsPerPage,
          offset
        });
      } else {
        response = await SubscriptionService.getAllPayments(itemsPerPage, offset);
      }
      
      if (response.success) {
        setPayments(response.payments || []);
        setTotalItems(response.total || response.payments?.length || 0);
      } else {
        setError('Failed to fetch payments');
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError(err.message || 'Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      fetchPayments();
      return;
    }

    const searchParams = {};
    
    if (searchType === 'invoice' || searchType === 'all') {
      searchParams.invoice_id = searchQuery;
    }
    if (searchType === 'transaction' || searchType === 'all') {
      searchParams.transaction_id = searchQuery;
    }
    if (searchType === 'user') {
      searchParams.user_id = searchQuery;
    }
    
    if (statusFilter) {
      searchParams.status = statusFilter;
    }
    
    setCurrentPage(1);
    await fetchPayments(searchParams);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchType('all');
    setStatusFilter('');
    setCurrentPage(1);
    fetchPayments();
  };

  const handleDownloadInvoice = (payment) => {
    const user = {
      username: payment.username,
      email: payment.email
    };
    generateInvoice(payment, user, { plan: { name: payment.plan_name } });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'usd') => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'succeeded':
        return <Badge bg="success">Paid</Badge>;
      case 'pending':
        return <Badge bg="warning" text="dark">Pending</Badge>;
      case 'failed':
        return <Badge bg="danger">Failed</Badge>;
      case 'refunded':
        return <Badge bg="info">Refunded</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-0">
            <CreditCard className="me-2" size={28} />
            Admin Billing & Payments
          </h2>
          <p className="text-muted">View and manage all user payments and transactions</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-4">
          {success}
        </Alert>
      )}

      {/* Search and Filter Card */}
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row className="g-3">
              <Col lg={4} md={6}>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={18} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by invoice ID, transaction ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </Col>
              
              <Col lg={2} md={6}>
                <Form.Select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                >
                  <option value="all">All Fields</option>
                  <option value="invoice">Invoice ID</option>
                  <option value="transaction">Transaction ID</option>
                  <option value="user">User ID</option>
                </Form.Select>
              </Col>
              
              <Col lg={2} md={6}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="succeeded">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </Form.Select>
              </Col>
              
              <Col lg={4} md={12}>
                <div className="d-flex gap-2">
                  <Button variant="primary" type="submit" className="d-flex align-items-center">
                    <Search size={18} className="me-1" />
                    Search
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    onClick={handleClearSearch}
                    className="d-flex align-items-center"
                  >
                    <RefreshCw size={18} className="me-1" />
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Payments Table */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FileText className="me-2" size={20} />
            Payment History
          </h5>
          <Badge bg="primary">{payments.length} payments</Badge>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading payments...</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-5">
              <CreditCard size={64} className="text-muted mb-3" />
              <h5>No payments found</h5>
              <p className="text-muted">
                {searchQuery ? 'Try adjusting your search criteria' : 'No payment records available'}
              </p>
            </div>
          ) : (
            <>
              <Table responsive hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Invoice ID</th>
                    <th>Transaction ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>
                        <strong>#{payment.id}</strong>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <User size={16} className="me-2 text-muted" />
                          <div>
                            <div className="fw-semibold">{payment.username || 'Unknown'}</div>
                            <small className="text-muted">{payment.email}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <Calendar size={16} className="me-2 text-muted" />
                          {formatDate(payment.created_at)}
                        </div>
                      </td>
                      <td>
                        {payment.description || payment.plan_name || 'Subscription'}
                      </td>
                      <td>
                        <strong>{formatCurrency(payment.amount, payment.currency)}</strong>
                      </td>
                      <td>{getStatusBadge(payment.status)}</td>
                      <td>
                        <small className="text-muted font-monospace">
                          {payment.stripe_invoice_id ? 
                            `${payment.stripe_invoice_id.substring(0, 20)}...` : 
                            'N/A'
                          }
                        </small>
                      </td>
                      <td>
                        <small className="text-muted font-monospace">
                          {payment.stripe_payment_intent_id ? 
                            `${payment.stripe_payment_intent_id.substring(0, 20)}...` : 
                            'N/A'
                          }
                        </small>
                      </td>
                      <td>
                        {payment.status === 'succeeded' && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="d-inline-flex align-items-center"
                            onClick={() => handleDownloadInvoice(payment)}
                            title="Download Invoice"
                          >
                            <Download size={14} className="me-1" />
                            Invoice
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First 
                      onClick={() => setCurrentPage(1)} 
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    />
                    
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      // Show first 3, last 3, and current page neighbors
                      if (
                        page <= 3 ||
                        page > totalPages - 3 ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === currentPage}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      }
                      // Show ellipsis
                      if (page === 4 || page === totalPages - 3) {
                        return <Pagination.Ellipsis key={page} />;
                      }
                      return null;
                    })}
                    
                    <Pagination.Next 
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Summary Stats */}
      {!loading && payments.length > 0 && (
        <Row className="mt-4">
          <Col md={3}>
            <Card className="text-center bg-light">
              <Card.Body>
                <h4 className="text-primary">
                  {payments.filter(p => p.status === 'succeeded').length}
                </h4>
                <small className="text-muted">Successful Payments</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center bg-light">
              <Card.Body>
                <h4 className="text-warning">
                  {payments.filter(p => p.status === 'pending').length}
                </h4>
                <small className="text-muted">Pending Payments</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center bg-light">
              <Card.Body>
                <h4 className="text-danger">
                  {payments.filter(p => p.status === 'failed').length}
                </h4>
                <small className="text-muted">Failed Payments</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center bg-light">
              <Card.Body>
                <h4 className="text-success">
                  {formatCurrency(
                    payments
                      .filter(p => p.status === 'succeeded')
                      .reduce((sum, p) => sum + (p.amount || 0), 0)
                  )}
                </h4>
                <small className="text-muted">Total Revenue (Visible)</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default AdminBillingPage;
